/**
 * NPC Health Monitor — Out-of-Combat Healing
 *
 * Watches party health. When an AI NPC with healing ability detects
 * an ally below 50%, it auto-casts Cure Wounds (if slots available).
 * 5-minute cooldown per target to avoid spam.
 */

import { useEffect, useRef } from 'react';
import { useCharacterStore } from '../stores/characterStore';
import { useCombatStore } from '../stores/combatStore';
import { useChatStore } from '../stores/chatStore';
import { useNpcMindStore } from '../stores/npcMindStore';
import { pickBark } from '@machina/core';
import type { NpcCombatContext, NpcDecision } from '@machina/core';

const COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
const CHECK_INTERVAL_MS = 3000; // Check every 3 seconds
const HEAL_THRESHOLD = 0.5; // Heal below 50%

/** Roll NdM+bonus dice string. */
function rollDice(dice: string): number {
  const match = dice.match(/(\d+)d(\d+)([+-]\d+)?/);
  if (!match) return 0;
  const count = parseInt(match[1]);
  const sides = parseInt(match[2]);
  const modifier = match[3] ? parseInt(match[3]) : 0;
  let total = 0;
  for (let i = 0; i < count; i++) {
    total += Math.floor(Math.random() * sides) + 1;
  }
  return Math.max(1, total + modifier);
}

export function useNpcHealthMonitor() {
  const cooldowns = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const interval = setInterval(() => {
      const combatState = useCombatStore.getState();
      // Only heal outside combat
      if (combatState.inCombat) return;

      const charStore = useCharacterStore.getState();
      const characters = Array.from(charStore.characters.values());

      // Find AI NPCs with healing spells
      const healerNpcs = characters.filter(c =>
        c.isNpc &&
        c.npcControlMode === 'ai' &&
        c.currentHp > 0 &&
        c.spellcasting &&
        (c.spellcasting.knownSpells?.some(s => s.toLowerCase().includes('cure wounds')) ||
         c.spellcasting.preparedSpells?.some(s => s.toLowerCase().includes('cure wounds')))
      );

      if (healerNpcs.length === 0) return;

      // Find wounded allies (non-NPC, below threshold)
      const wounded = characters
        .filter(c => !c.isNpc && c.currentHp > 0 && c.maxHp > 0 && c.currentHp / c.maxHp < HEAL_THRESHOLD)
        .sort((a, b) => (a.currentHp / a.maxHp) - (b.currentHp / b.maxHp)); // Most wounded first

      if (wounded.length === 0) return;

      const now = Date.now();

      for (const healer of healerNpcs) {
        if (!healer.spellcasting) continue;

        // Find lowest available slot
        let slotLevel: number | null = null;
        for (let lvl = 1; lvl <= 3; lvl++) {
          const key = String(lvl);
          const total = healer.spellcasting.spellSlots?.[key] ?? 0;
          const used = healer.spellcasting.spellSlotsUsed?.[key] ?? 0;
          if (used < total) {
            slotLevel = lvl;
            break;
          }
        }
        if (slotLevel === null) continue; // No slots

        // Find first wounded ally not on cooldown
        for (const target of wounded) {
          const cdKey = `${healer.id}:${target.id}`;
          const lastHeal = cooldowns.current.get(cdKey) ?? 0;
          if (now - lastHeal < COOLDOWN_MS) continue;

          // Heal! Life Domain bonus: +2 + spell_level
          const wisMod = Math.floor((healer.abilityScores.wisdom - 10) / 2);
          const lifeDomainBonus = 2 + slotLevel;
          const healingDice = `${slotLevel}d8+${wisMod + lifeDomainBonus}`;
          const healAmount = rollDice(healingDice);

          charStore.healCharacter(target.id, healAmount);

          // Consume spell slot
          const key = String(slotLevel);
          const newUsed = { ...(healer.spellcasting.spellSlotsUsed ?? {}) };
          newUsed[key] = (newUsed[key] ?? 0) + 1;
          charStore.updateCharacter(healer.id, {
            spellcasting: { ...healer.spellcasting, spellSlotsUsed: newUsed },
          });

          // Post chat messages
          const bark = pickBark('heal') ?? 'Hold still.';
          const chatStore = useChatStore.getState();
          chatStore.addSystemMessage(
            `[${healer.name}] casts Cure Wounds on ${target.name} — heals ${healAmount} HP (${healingDice})`
          );
          chatStore.addNpcMessage({
            npcId: healer.id,
            npcName: healer.name,
            npcTitle: (healer as any).npcTitle,
            factionColor: (healer as any).npcFactionColor,
            message: bark,
          });

          // Push to NPC mind store for GM tray
          const healDecision: NpcDecision = {
            action: {
              type: 'heal',
              spellName: 'Cure Wounds',
              spellLevel: slotLevel,
              targetId: target.id,
              targetName: target.name,
              healingDice,
            },
            bark,
            reasoning: `Out-of-combat heal: ${target.name} at ${Math.round((target.currentHp / target.maxHp) * 100)}% HP`,
          };
          const healCtx: NpcCombatContext = {
            selfId: healer.id,
            selfName: healer.name,
            selfHp: healer.currentHp,
            selfMaxHp: healer.maxHp,
            selfAc: healer.armorClass,
            spellSlots: {
              total: healer.spellcasting.spellSlots ?? {},
              used: newUsed,
            },
            concentratingOn: healer.concentratingOn ?? null,
            spellSaveDc: 8 + healer.proficiencyBonus + Math.floor((healer.abilityScores.wisdom - 10) / 2),
            spellAttackBonus: healer.proficiencyBonus + Math.floor((healer.abilityScores.wisdom - 10) / 2),
            allies: characters
              .filter(c => c.id !== healer.id && !c.isNpc)
              .map(c => ({
                id: c.id,
                name: c.name,
                currentHp: c.currentHp,
                maxHp: c.maxHp,
                hpPercent: c.maxHp > 0 ? c.currentHp / c.maxHp : 0,
                isDown: c.currentHp <= 0,
              })),
            enemies: [],
            round: 0,
          };
          useNpcMindStore.getState().pushState(healer.id, healer.name, healDecision, healCtx);

          // Set cooldown
          cooldowns.current.set(cdKey, now);

          // One heal per check interval per healer
          break;
        }
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);
}
